import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { Save, MessageSquare } from 'lucide-react';
import ExportMonthDialog from '../components/ExportMonthDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  'Comanda plasata',
  'Comanda Acceptata',
  'Comanda in livrare',
  'Comanda livrata'
];

const statusStyles = {
  'Comanda plasata': 'bg-blue-100 text-blue-800 border border-blue-200',
  'Comanda Acceptata': 'bg-orange-100 text-orange-800 border border-orange-200',
  'Comanda in livrare': 'bg-purple-100 text-purple-800 border border-purple-200',
  'Comanda livrata': 'bg-green-100 text-green-800 border border-green-200'
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminNotes, setAdminNotes] = useState({});

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/orders`, { withCredentials: true });
      setOrders(data);
      const notesMap = {};
      data.forEach((order) => {
        notesMap[order.id] = order.admin_note || '';
      });
      setAdminNotes(notesMap);
    } catch (error) {
      toast.error('Eroare la încărcarea comenzilor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success('Status actualizat cu succes');
      fetchOrders();
    } catch (error) {
      toast.error('Eroare la actualizarea statusului');
    }
  };

  const saveAdminNote = async (orderId) => {
    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/admin-note`,
        { admin_note: adminNotes[orderId] || '' },
        { withCredentials: true }
      );
      toast.success('Notă salvată cu succes');
      fetchOrders();
    } catch (error) {
      toast.error('Eroare la salvarea notei');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <p style={{ fontFamily: 'IBM Plex Sans' }}>Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Gestionare Comenzi
          </h1>
          <ExportMonthDialog
            endpoint="/admin/export/orders"
            filenamePrefix="comenzi"
            triggerLabel="Export lunar Excel"
            testId="export-orders-button"
          />
        </div>

        {orders.length === 0 ? (
          <p className="text-slate-600" style={{ fontFamily: 'IBM Plex Sans' }}>
            Nu există comenzi plasate încă.
          </p>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <Card
                key={order.id}
                className="border border-slate-200 shadow-sm"
                data-testid={`admin-order-${order.id}`}
              >
                <CardHeader className="bg-slate-50 border-b border-slate-200">
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
                    <div>
                      <CardTitle style={{ fontFamily: 'Outfit' }}>
                        Comandă #{order.id.substring(0, 8).toUpperCase()}
                      </CardTitle>
                      <p className="text-sm text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans' }}>
                        Client: <span className="font-semibold">{order.user_name}</span> ({order.user_email})
                      </p>
                      <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans' }}>
                        Plasată la: {format(new Date(order.created_at), 'dd MMMM yyyy, HH:mm', { locale: ro })}
                      </p>
                    </div>
                    <Badge className={statusStyles[order.status]} data-testid={`admin-order-status-${order.id}`}>
                      {order.status}
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>
                        Produse:
                      </h4>
                      <div className="space-y-2">
                        {order.items.map((item) => (
                          <div
                            key={item.product_id || item.name}
                            className="flex justify-between text-slate-700 bg-slate-50 p-3 rounded"
                            style={{ fontFamily: 'IBM Plex Sans' }}
                          >
                            <span>
                              {item.name} x {item.quantity}
                            </span>
                            <span className="font-semibold">
                              {item.is_quote_only
                                ? <span className="text-orange-600">Cere ofertă</span>
                                : `${(item.price * item.quantity).toFixed(2)} RON`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {order.client_note && (
                      <div
                        className="p-4 bg-blue-50 border border-blue-200 rounded-md"
                        data-testid={`admin-order-client-note-${order.id}`}
                      >
                        <div className="flex items-center space-x-2 mb-1">
                          <MessageSquare className="w-4 h-4 text-blue-900" />
                          <p className="text-xs font-semibold text-blue-900" style={{ fontFamily: 'IBM Plex Sans' }}>
                            Instrucțiuni de la client:
                          </p>
                        </div>
                        <p className="text-sm text-slate-700 ml-6" style={{ fontFamily: 'IBM Plex Sans' }}>
                          {order.client_note}
                        </p>
                      </div>
                    )}

                    <div className="border-t pt-4 flex justify-between items-center">
                      <span className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                        Total:
                      </span>
                      <span
                        className="text-2xl font-bold text-blue-900"
                        style={{ fontFamily: 'Outfit' }}
                        data-testid={`admin-order-total-${order.id}`}
                      >
                        {order.total.toFixed(2)} RON
                      </span>
                    </div>

                    <div className="border-t pt-4">
                      <label
                        className="text-sm font-semibold mb-2 block"
                        style={{ fontFamily: 'IBM Plex Sans' }}
                      >
                        Modifică Status:
                      </label>
                      <Select
                        value={order.status}
                        onValueChange={(value) => updateStatus(order.id, value)}
                      >
                        <SelectTrigger
                          className="w-full"
                          data-testid={`status-select-${order.id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map((status) => (
                            <SelectItem
                              key={status}
                              value={status}
                              data-testid={`status-option-${status}`}
                            >
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="border-t pt-4">
                      <label
                        className="flex items-center space-x-2 text-sm font-semibold mb-2"
                        style={{ fontFamily: 'IBM Plex Sans' }}
                      >
                        <MessageSquare className="w-4 h-4 text-orange-600" />
                        <span>Notă internă / Mesaj pentru client:</span>
                      </label>
                      <Textarea
                        value={adminNotes[order.id] || ''}
                        onChange={(e) =>
                          setAdminNotes({ ...adminNotes, [order.id]: e.target.value })
                        }
                        placeholder="Ex: Ambalare specială, livrare programată, contactare rapidă..."
                        rows={2}
                        className="mb-2 resize-none"
                        data-testid={`admin-note-input-${order.id}`}
                      />
                      <Button
                        onClick={() => saveAdminNote(order.id)}
                        size="sm"
                        className="bg-orange-600 hover:bg-orange-700 text-white"
                        data-testid={`save-admin-note-${order.id}`}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Salvează Nota
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrders;
